import { useEffect, useState } from "react";
import API from "../api/axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Navigation,
  Package,
  ImageOff,
  Clock,
  Facebook,
  Instagram,
  Music4,
  MessageCircle,
  Globe,
  Images,
  Store,
  Eye,
} from "lucide-react";
import MobileBottomBar from "../components/MobileBottomBar";
import { toast } from "react-toastify";
import { isSedeOpen } from "../utils/sedeStatus";
import { openMapsNavigation } from "../utils/openMapsNavigation";
import { getImageUrl } from "../utils/getImageUrl";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import CartQtyControl from "../components/CartQtyControl";

const getId = (obj) => obj?.id ?? obj?._id;

export default function SedeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sede,        setSede]       = useState(null);
  const [company,     setCompany]    = useState(null);
  const [products,    setProducts]   = useState([]);
  const [secciones,   setSecciones]  = useState([]);
  const [activeTab,   setActiveTab]  = useState("all");
  const [showGallery, setShowGallery] = useState(false);
  const [quickView,   setQuickView]  = useState(null);

  useEffect(() => {
    API.get(`/sedes/${id}`)
      .then((res) => {
        const s = res.data?.data?.sede ?? res.data?.sede ?? res.data;
        setSede(s);
        const companyId = s?.companyId ?? s?.company?.id ?? s?.company;
        if (companyId) return API.get(`/companies/by-id/${companyId}`);
      })
      .then((res) => {
        if (!res) return;
        const c = res.data?.data?.company ?? res.data?.company ?? res.data;
        setCompany(c);
      })
      .catch((err) => console.error(err));
  }, [id]);

  useEffect(() => {
    API.get(`/products/sede/${id}`)
      .then((res) => {
        const raw = res.data?.data?.products ?? res.data?.products ?? res.data;
        setProducts(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    const companyId = getId(company);
    if (!companyId) return;
    API.get(`/seccions/company/${companyId}`)
      .then((res) => {
        const raw =
          res.data?.data?.secciones ?? res.data?.secciones ??
          res.data?.data?.seccions  ?? res.data?.seccions  ?? res.data;
        setSecciones(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setSecciones([]));
  }, [company]);

  if (!sede || !company) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
        <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Cargando sede...</p>
      </div>
    );
  }

  const sedeStatus = isSedeOpen(sede);
  const images     = sede.images || [];
  const coverIndex = images.findIndex((i) => i.isCover);
  const coverImage = coverIndex >= 0 ? images[coverIndex].url : images[0]?.url;

  const getSocial = (key) => sede[key] ?? sede.social?.[key] ?? "";
  const sedeLat = sede.lat ?? sede.location?.lat;
  const sedeLng = sede.lng ?? sede.location?.lng;

  const socialLinks = [
    { key: "facebook",  icon: Facebook,      label: "Facebook"  },
    { key: "instagram", icon: Instagram,     label: "Instagram" },
    { key: "tiktok",    icon: Music4,        label: "TikTok"    },
    { key: "whatsapp",  icon: MessageCircle, label: "WhatsApp"  },
    { key: "web",       icon: Globe,         label: "Web"       },
  ].filter((s) => getSocial(s.key)?.trim() !== "");

  const handleDirections = () => {
    if (!sedeLat || !sedeLng) { toast.info("Esta sede no tiene ubicación registrada"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => openMapsNavigation(sedeLat, sedeLng, pos.coords.latitude, pos.coords.longitude),
      ()    => openMapsNavigation(sedeLat, sedeLng),
    );
  };

  const filteredProducts =
    activeTab === "all"
      ? products
      : products.filter((p) => {
          const secId = p.seccionId ?? p.seccion?.id ?? p.seccion?._id ?? p.seccion;
          return secId === activeTab;
        });

  return (
    <div className="min-h-screen bg-slate-50 pb-28 lg:pb-8">

      {/* ── Header con fondo azul/slate ─────────────────────── */}
      <div
        className="relative"
        style={{ background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 60%, #60a5fa 100%)" }}
      >
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 z-10 w-8 h-8 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/25 transition"
        >
          <ArrowLeft size={16} />
        </button>

        {/* Cover image (si hay) */}
        {coverImage && (
          <div
            className="w-full h-32 sm:h-40 overflow-hidden cursor-pointer relative"
            onClick={() => setShowGallery(true)}
          >
            <img
              src={getImageUrl(coverImage)}
              alt={sede.name}
              className="w-full h-full object-cover opacity-40"
            />
            {images.length > 1 && (
              <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/40 text-white text-xs px-2 py-1 rounded-lg">
                <Images size={11} />
                <span>{images.length} fotos</span>
              </div>
            )}
          </div>
        )}

        {/* Sede label + name */}
        <div className={`px-4 ${coverImage ? "pt-3" : "pt-12"} pb-5`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Store size={13} className="text-blue-300" />
            <span className="text-xs font-semibold text-blue-300 uppercase tracking-widest">Sede</span>
          </div>
          <h1 className="text-xl font-bold text-white leading-tight">{sede.name}</h1>

          {/* Open/closed pill + horario */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
              sedeStatus.open
                ? "bg-green-500/20 text-green-300 border border-green-500/30"
                : "bg-red-500/20 text-red-300 border border-red-500/30"
            }`}>
              {sedeStatus.open ? "● Abierto ahora" : "● Cerrado ahora"}
            </span>
            {sedeStatus.message && (
              <span className="text-xs text-white/60 flex items-center gap-1">
                <Clock size={11} />
                {sedeStatus.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Info card ──────────────────────────────────────────── */}
      <div className="bg-white shadow-sm mb-2">
        <div className="px-3 sm:px-4 py-3">

          {/* Empresa row */}
          <button
            onClick={() => navigate(`/company/${getId(company)}`)}
            className="w-full flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100 hover:opacity-80 transition text-left"
          >
            <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
              {company.logo ? (
                <img
                  src={getImageUrl(company.logo)}
                  alt={company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 size={18} className="text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400">Pertenece a</p>
              <p className="text-sm font-bold text-gray-800 truncate">{company.name}</p>
            </div>
            <span className="text-xs text-blue-500 font-semibold whitespace-nowrap">Ver tienda →</span>
          </button>

          {/* Dirección + teléfono */}
          <div className="flex flex-col gap-1.5 mb-3">
            {sede.address && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin size={14} className="flex-shrink-0 mt-0.5 text-slate-400" />
                <span>{sede.address}</span>
              </div>
            )}
            {sede.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} className="flex-shrink-0 text-slate-400" />
                <span>{sede.phone}</span>
              </div>
            )}
          </div>

          {/* Botones: Cómo llegar + redes */}
          {((sedeLat && sedeLng) || socialLinks.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              {sedeLat && sedeLng && (
                <button
                  onClick={handleDirections}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition shadow-sm"
                >
                  <Navigation size={13} /> Cómo llegar
                </button>
              )}
              {socialLinks.map(({ key, icon: Icon, label }) => {
                const val = getSocial(key);
                const url = key === "whatsapp" ? `https://wa.me/${val}` : val;
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={label}
                    className="w-8 h-8 flex items-center justify-center bg-slate-100 hover:bg-blue-50 text-slate-500 hover:text-blue-600 rounded-lg transition"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Productos ────────────────────────────────────────── */}
      <div className="bg-white shadow-sm">
        <div className="px-3 sm:px-4 pt-3 pb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Package size={14} className="text-blue-500" />
            Productos en esta sede
          </h2>
          <span className="text-xs text-gray-400">
            {filteredProducts.length} resultado{filteredProducts.length !== 1 ? "s" : ""}
          </span>
        </div>

        {secciones.length > 0 && (
          <div className="overflow-x-auto px-3 sm:px-4 pb-2 flex gap-1.5 scrollbar-hide">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition ${
                activeTab === "all"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todos
            </button>
            {secciones.map((s) => (
              <button
                key={getId(s)}
                onClick={() => setActiveTab(getId(s))}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition ${
                  activeTab === getId(s)
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        <div className="px-3 sm:px-4 pb-4">
          <AnimatePresence mode="wait">
            {filteredProducts.length > 0 ? (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5"
              >
                {filteredProducts.map((p, i) => {
                  const hasOffer = p.offerPrice && Number(p.offerPrice) < Number(p.price);
                  const discount = hasOffer
                    ? Math.round((1 - Number(p.offerPrice) / Number(p.price)) * 100)
                    : 0;

                  return (
                    <motion.div
                      key={getId(p)}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => navigate(`/product/${getId(p)}`)}
                      className={`relative bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer ${
                        !sedeStatus.open ? "opacity-75" : ""
                      }`}
                    >
                      <div className="relative w-full aspect-square bg-gray-100">
                        {p.images?.[0] ? (
                          <img
                            src={getImageUrl(p.images[0])}
                            alt={p.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <ImageOff size={20} className="text-gray-300" />
                            <span className="text-xs text-gray-300">Sin imagen</span>
                          </div>
                        )}

                        {hasOffer && (
                          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-md shadow">
                            -{discount}%
                          </div>
                        )}

                        {!sedeStatus.open && (
                          <div className="absolute inset-0 flex items-end justify-start p-1.5">
                            <span className="px-1.5 py-0.5 bg-gray-900/70 backdrop-blur-sm text-white text-xs font-semibold rounded-md flex items-center gap-1">
                              <Clock size={9} />
                              {sedeStatus.message || "Cerrado"}
                            </span>
                          </div>
                        )}

                        {p.stock <= 0 && (
                          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-gray-700 text-white text-xs font-bold rounded-md">
                            Sin stock
                          </div>
                        )}
                      </div>

                      <div className="p-2.5">
                        <h3 className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">
                          {p.title}
                        </h3>
                        {hasOffer ? (
                          <div>
                            <p className="text-sm font-bold text-supernova-600">
                              ${Number(p.offerPrice).toLocaleString("es-CO")}
                            </p>
                            <p className="text-xs text-gray-400 line-through">
                              ${Number(p.price).toLocaleString("es-CO")}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm font-bold text-supernova-600">
                            ${Number(p.price).toLocaleString("es-CO")}
                          </p>
                        )}

                        <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setQuickView({ product: p, isOpen: sedeStatus.open, closedMessage: sedeStatus.message })}
                            className="p-1.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
                            title="Ver descripción"
                          >
                            <Eye size={12} className="text-gray-500" />
                          </button>
                          <CartQtyControl
                            product={p}
                            isOpen={sedeStatus.open}
                            size="sm"
                            addClassName="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-12 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <Package size={24} className="text-slate-300" />
                </div>
                <p className="text-sm font-semibold text-gray-400">No hay productos en esta sección</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Galería lightbox ─────────────────────────────────── */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            className="fixed inset-0 bg-black/92 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowGallery(false)}
          >
            <button
              className="absolute top-4 right-4 w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition z-10"
              onClick={() => setShowGallery(false)}
            >
              ✕
            </button>
            <div
              className="flex gap-4 overflow-x-auto px-6 snap-x snap-mandatory w-full max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, i) => (
                <img
                  key={i}
                  src={getImageUrl(img.url)}
                  alt=""
                  className="max-h-[85vh] mx-auto snap-center rounded-xl object-contain flex-shrink-0"
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MobileBottomBar />

      {quickView && (
        <ProductQuickViewModal
          product={quickView.product}
          isOpen={quickView.isOpen}
          closedMessage={quickView.closedMessage}
          onClose={() => setQuickView(null)}
        />
      )}
    </div>
  );
}
